import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { StatusBadge, PriorityBadge, SourceBadge } from '../components/StatusBadge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ClipboardList, Clock, Wrench, Eye, CheckCircle2, XCircle, Plus, Info } from 'lucide-react';
import { toast } from 'sonner';

const BUCKET_LABELS = {
  todo: 'myMaintenance.todo',
  doing: 'myMaintenance.doing',
  review: 'myMaintenance.review',
  done: 'myMaintenance.done',
};

const CARDS = [
  { key: 'total_submitted', labelKey: 'myMaintenance.totalSubmitted', icon: ClipboardList, tone: 'text-foreground' },
  { key: 'todo', labelKey: 'myMaintenance.todo', icon: Clock, tone: 'text-brand' },
  { key: 'doing', labelKey: 'myMaintenance.doing', icon: Wrench, tone: 'text-violet-500' },
  { key: 'review', labelKey: 'myMaintenance.review', icon: Eye, tone: 'text-amber-500' },
  { key: 'done', labelKey: 'myMaintenance.done', icon: CheckCircle2, tone: 'text-emerald-500' },
  { key: 'rejected', labelKey: 'myMaintenance.rejected', icon: XCircle, tone: 'text-rose-500' },
];

export default function MyMaintenancePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [sumRes, listRes] = await Promise.all([
        api.get('/work-requests/my-summary'),
        api.get('/work-requests', { params: { my_requests: true } }),
      ]);
      setSummary(sumRes.data);
      setRequests(listRes.data);
    } catch (err) {
      toast.error(t('toast.error'));
    } finally {
      setLoading(false);
    }
  };

  const progressLabel = (wr) => {
    if (wr.wo_bucket) return t(BUCKET_LABELS[wr.wo_bucket]);
    if (wr.status === 'REJECTED') return t('myMaintenance.rejected');
    if (wr.status === 'CLOSED') return t('myMaintenance.done');
    return t('myMaintenance.awaiting');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div data-testid="my-maintenance-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-3xl sm:text-4xl text-foreground tracking-tight">
            {t('myMaintenance.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-xl">{t('myMaintenance.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/work-requests')}
          className="bg-brand hover:bg-brand-hover text-white"
          data-testid="my-maintenance-new-request-btn"
        >
          <Plus className="w-4 h-4 me-2" /> {t('myMaintenance.newRequest')}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="my-maintenance-cards">
        {CARDS.map(card => {
          const Icon = card.icon;
          return (
            <Card key={card.key} className="bg-card border-border shadow-sm hover:border-brand/40 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(card.labelKey)}
                  </span>
                  <Icon className={`w-4 h-4 ${card.tone}`} />
                </div>
                <p
                  className={`font-barlow font-bold text-3xl mt-2 ${card.tone}`}
                  data-testid={`my-maintenance-count-${card.key}`}
                >
                  {summary?.[card.key] ?? 0}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>{t('myMaintenance.readOnlyNote')}</span>
      </div>

      {/* Requests table */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-0">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <h2 className="font-barlow font-semibold text-base md:text-lg text-foreground">
              {t('myMaintenance.myRequests')}
            </h2>
            <Badge variant="secondary" data-testid="my-maintenance-row-count">{requests.length}</Badge>
          </div>

          {requests.length === 0 ? (
            <div className="py-16 text-center px-6">
              <ClipboardList className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">{t('myMaintenance.noRequests')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('myMaintenance.noRequestsHint')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {[
                      'myMaintenance.requestNo', 'myMaintenance.woNo', 'common.name', 'common.branch',
                      'common.room', 'common.asset', 'workRequest.priority', 'common.status',
                      'source.label', 'workOrder.assignedTo', 'common.createdAt',
                      'myMaintenance.startedAt', 'myMaintenance.closedAt', 'myMaintenance.duration',
                      'myMaintenance.latestUpdate',
                    ].map(k => (
                      <th key={k} className="text-start px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                        {t(k)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.map(wr => (
                    <tr
                      key={wr.id}
                      className="border-b border-border hover:bg-muted/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/work-requests/${wr.id}`)}
                      data-testid={`my-request-row-${wr.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{wr.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-brand" data-testid={`my-request-wo-${wr.id}`}>
                        {wr.work_order_id ? wr.work_order_id.slice(0, 8) : '—'}
                      </td>
                      <td className="px-4 py-3 text-foreground font-medium max-w-[220px] truncate">
                        {wr.title || wr.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{wr.branch_name || '—'}</td>
                      <td className="px-4 py-3 text-foreground whitespace-nowrap" data-testid={`my-request-room-${wr.id}`}>
                        {wr.room_name || <span className="text-muted-foreground italic">{t('common.noRoom')}</span>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[160px] truncate">
                        {(i18n.language === 'ar' && wr.asset_name_ar) ? wr.asset_name_ar : (wr.asset_name || '—')}
                      </td>
                      <td className="px-4 py-3">{wr.priority ? <PriorityBadge priority={wr.priority} /> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap" data-testid={`my-request-progress-${wr.id}`}>
                        {wr.wo_status ? <StatusBadge status={wr.wo_status} /> : <StatusBadge status={wr.status} />}
                        <span className="block text-[10px] text-muted-foreground mt-1">{progressLabel(wr)}</span>
                      </td>
                      <td className="px-4 py-3"><SourceBadge source={wr.maintenance_source} compact /></td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {wr.wo_assigned_to_name || t('workOrder.unassigned')}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(wr.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {wr.wo_started_at ? formatDate(wr.wo_started_at) : t('myMaintenance.notStarted')}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {wr.wo_closed_at ? formatDate(wr.wo_closed_at) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {wr.wo_duration_minutes != null ? `${wr.wo_duration_minutes} min` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(wr.wo_updated_at || wr.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
